/**
 * LA MODALE DELL'ESITO — l'incontro deciso e la corsa finita, detti al giocatore in qualunque schermata.
 *
 * 🔴 **La richiesta** (Luca, 2026-09-29T11:51:52Z, risposta a L-2484), testuale: «Una volta fatto
 * risultato vinto/perso match piazzamento/vittoria torneo (o sit) bisogna che venga mostrata una modale
 * con l'informazione.» Le regole sotto sono del seggio (tma-orcr, dispatch I-124 e ok 12:03:05Z), non di
 * Luca.
 *
 * **Da dove viene il dato** (misurato su `qlash-limulo@origin/staging` = `5ffbb09`, sanitizer del BFF):
 *  · l'INNESCO e' la lettura delle azioni che la barra fa gia' (`GET /v1/me/tournaments/actions`): porta
 *    TUTTI gli incontri `pending` delle mie parti nei tornei aperti, anche quelli senza niente da fare
 *    (`awaiting-*`, `disputed`, `double-no-show`). ⇒ un `matchId` che SPARISCE e' uscito da `pending`,
 *    oppure il suo torneo si e' chiuso. Con `truncated: true` la lista non e' completa e la sparizione
 *    non vuol dire niente: non si usa;
 *  · l'ESITO e' la scheda dell'incontro: `status === 'completed'` solo a incontro chiuso, e
 *    `result.winnerPlayerId` e' la PARTE vincente (anche col forfait, il no-show e il tavolino, che
 *    escono `resultType: 'forfeit'` col vincitore). Il mio lato lo dice `players.a/b.isMe`;
 *  · la FINE DELLA CORSA esiste solo a torneo terminale: in eliminazione diretta la classifica resta
 *    `unavailable` finche' il tabellone non e' finito, e nessun campo dice «eliminato» durante il torneo.
 *    ⇒ nel sit&go chi perde la semifinale vede «Hai perso» subito e il posto a fine torneo: DUE modali,
 *    deciso dal seggio (punto A). Per questo la sconfitta non dice mai «sei fuori», e il posto non dice
 *    «Eliminato»: in swiss o in un girone non e' vero, e il `format` non arriva (DECISIONE del seggio sulla
 *    revisione di #503, 🟡6: titolo neutro «N° posto» finche' il BFF non manda `format` o la corsa, kb#282);
 *  · l'ORA della fine corsa non esiste (nessun `completedAt`): vale il `recordedAt` del mio ultimo
 *    incontro, esatto per chi vince e approssimato per chi e' uscito prima (seggio, punto B);
 *  · nessun campo dice quale premio hai vinto ⇒ in modale non ci sono premi.
 *
 * **La linea di base**: una modale nasce SOLO da un incontro che questa app ha VISTO `pending`. Al primo
 * avvio dopo il rilascio l'insieme e' vuoto, e un risultato vecchio non esce mai. In piu' l'esito deve
 * essere piu' recente della base e delle ultime 24 ore.
 *
 * ⚠️ Pura e senza `import.meta.env`, `window` o storage: la memoria e l'ora le passa chi la usa.
 */
import type { LimuloMatch, LimuloTournament } from '../../types/limulo.ts';
import type { LimuloAzioniDelMotore } from './azionePersistente.ts';
import { formatData, formatOra } from '../fmt.ts';
import { storicoEsitoOf } from './model.ts';
import { presentazioneDi, type Presentazione } from './presentazione.ts';

/** Oltre questo, un esito non si mostra piu' (seggio, decisione 3). */
export const ESITI_FINESTRA_MS = 24 * 60 * 60 * 1000;

/** I tetti della memoria: una lista che cresce per sempre in localStorage e' un guasto lento. */
export const ESITI_TETTO_OSSERVATI = 200;
export const ESITI_TETTO_CORSE = 50;
export const ESITI_TETTO_VISTE = 200;

/** La chiave di storage, una per utente (`sub` di Logto). */
export const chiaveMemoriaEsiti = (sub: string): string => `qlash.esiti.v1.${sub}`;

/** Una corsa in un torneo: cosa ho visto e cosa so del mio ultimo incontro. */
export interface CorsaOsservata {
  /** Il nome del torneo, dalla voce del motore. */
  torneo: string | null;
  /** La mia parte, dal mio ultimo incontro deciso: serve a trovare la mia riga in classifica. */
  partyId: string | null;
  /** `recordedAt` del mio ultimo incontro deciso (ms), l'ora della fine corsa (punto B). */
  ultimo: number | null;
  /** L'avversario del mio ultimo incontro deciso: lo dice la modale della coda 1v1. */
  avversario: string | null;
}

/**
 * Un esito gia' mostrato, con l'ORA DELL'ESITO (`recordedAt`), non quella della vista: serve a potare.
 * Una vista piu' vecchia di `ESITI_FINESTRA_MS` non protegge niente, perche' `mostrabile` scarta gia'
 * quell'esito per eta' (revisione di #503, 🟡9).
 */
export interface Vista {
  chiave: string;
  quando: number;
}

export interface MemoriaEsiti {
  /** Il primo istante in cui questa app ha osservato, per questo utente. */
  base: number;
  /** Gli incontri visti `pending`: `matchId` → torneo. */
  osservati: Record<string, { tournamentId: string; torneo: string | null }>;
  /** I tornei in cui ho (o ho avuto) incontri osservati. */
  corse: Record<string, CorsaOsservata>;
  /** Gli esiti gia' mostrati, dal piu' vecchio al piu' recente. */
  viste: Vista[];
}

export const memoriaNuova = (ora: number): MemoriaEsiti => ({ base: ora, osservati: {}, corse: {}, viste: [] });

const record = (v: unknown): Record<string, unknown> | null =>
  v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
const testo = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);
const numero = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/**
 * La memoria letta dallo storage. Una forma che non riconosco NON diventa una memoria vuota con la base
 * di adesso che finge di essere quella vecchia: diventa una memoria nuova, cioe' «non ho visto niente»,
 * che al peggio tace un esito — mai lo inventa.
 */
export function memoriaDa(grezza: unknown, ora: number): MemoriaEsiti {
  const r = record(grezza);
  const base = numero(r?.base);
  if (r === null || base === null) return memoriaNuova(ora);
  const osservati: MemoriaEsiti['osservati'] = {};
  for (const [matchId, v] of Object.entries(record(r.osservati) ?? {})) {
    const o = record(v);
    const tournamentId = testo(o?.tournamentId);
    if (tournamentId !== null) osservati[matchId] = { tournamentId, torneo: testo(o?.torneo) };
  }
  const corse: MemoriaEsiti['corse'] = {};
  for (const [tournamentId, v] of Object.entries(record(r.corse) ?? {})) {
    const c = record(v);
    if (c === null) continue;
    corse[tournamentId] = {
      torneo: testo(c.torneo),
      partyId: testo(c.partyId),
      ultimo: numero(c.ultimo),
      avversario: testo(c.avversario),
    };
  }
  const viste: Vista[] = [];
  for (const v of Array.isArray(r.viste) ? r.viste : []) {
    // Una chiave nuda (forma vecchia, o scritta a mano) vale come vista ADESSO: tenuta, non inventata vecchia.
    if (typeof v === 'string') viste.push({ chiave: v, quando: ora });
    const o = record(v);
    const chiave = testo(o?.chiave);
    const quando = numero(o?.quando);
    if (chiave !== null && quando !== null) viste.push({ chiave, quando });
  }
  return { base, osservati, corse, viste };
}

/** Tiene gli ULTIMI `tetto` elementi di un oggetto in ordine d'inserimento. */
function tosa<T>(o: Record<string, T>, tetto: number): Record<string, T> {
  const voci = Object.entries(o);
  return voci.length <= tetto ? o : Object.fromEntries(voci.slice(voci.length - tetto));
}

export interface Sparito {
  matchId: string;
  tournamentId: string;
}

/**
 * Una lettura delle azioni → la memoria aggiornata e gli incontri SPARITI da quando li avevo visti.
 * Senza lettura (`null`: errore o forma sconosciuta) non cambia niente: un errore non e' «sparito tutto».
 */
export function osserva(
  memoria: MemoriaEsiti,
  azioni: LimuloAzioniDelMotore | null,
): { memoria: MemoriaEsiti; spariti: Sparito[] } {
  if (azioni === null) return { memoria, spariti: [] };
  const osservati = { ...memoria.osservati };
  const corse = { ...memoria.corse };
  const presenti = new Set<string>();
  for (const v of azioni.items) {
    if (v.matchId === null) continue;
    presenti.add(v.matchId);
    osservati[v.matchId] = { tournamentId: v.tournamentId, torneo: v.tournamentName ?? osservati[v.matchId]?.torneo ?? null };
    const c = corse[v.tournamentId];
    corse[v.tournamentId] = c
      ? { ...c, torneo: v.tournamentName ?? c.torneo }
      : { torneo: v.tournamentName, partyId: null, ultimo: null, avversario: null };
  }
  const spariti = azioni.truncated
    ? []
    : Object.entries(memoria.osservati)
        .filter(([matchId]) => !presenti.has(matchId))
        .map(([matchId, o]) => ({ matchId, tournamentId: o.tournamentId }));
  return {
    memoria: { ...memoria, osservati: tosa(osservati, ESITI_TETTO_OSSERVATI), corse: tosa(corse, ESITI_TETTO_CORSE) },
    spariti,
  };
}

/** Un incontro deciso, dal mio lato. */
export interface IncontroDeciso {
  vinto: boolean;
  /** La mia parte. */
  partyId: string;
  avversario: string | null;
  /** `result.recordedAt`, in ms. */
  quando: number;
}

/**
 * La scheda dell'incontro → l'esito dal mio lato, o `null` se non ce n'e' uno da dire:
 *  · non chiuso (`status !== 'completed'`: proposta, disputa, doppia assenza allo staff);
 *  · senza vincitore (pareggio, bye, forfait di dominio: `winnerPlayerId` nullo);
 *  · senza il mio lato (`isMe` assente = «non lo so», mai «non sono io»);
 *  · senza l'ora (serve alla finestra delle 24 ore).
 */
export function incontroDecisoOf(match: LimuloMatch | null): IncontroDeciso | null {
  if (match === null || match.status !== 'completed' || match.isBye || match.result === null) return null;
  const vincitore = testo(match.result.winnerPlayerId);
  const quando = Date.parse(match.result.recordedAt ?? '');
  if (vincitore === null || !Number.isFinite(quando)) return null;
  const { a, b } = match.players;
  const mio = a?.isMe === true ? a : b?.isMe === true ? b : null;
  const altro = mio === a ? b : a;
  if (mio === null || !mio.id) return null;
  return { vinto: vincitore === mio.id, partyId: mio.id, avversario: testo(altro?.displayName), quando };
}

/** Un torneo che non cambiera' piu': la stessa enumerazione dello storico (`storicoEsitoOf`). */
export const torneoTerminale = (t: Pick<LimuloTournament, 'status'> | null): boolean =>
  t !== null && storicoEsitoOf(t) !== 'sconosciuto';

/**
 * Il mio posto nella classifica FINALE, o `null`. La riga si trova per la PARTE (`id` della riga =
 * `partyId`), mai per nome. Una classifica non definitiva (`final !== true`) non da' un posto.
 */
export function postoFinaleOf(
  classifica: { final: boolean | null; rows: { position: number | null; raw: Record<string, unknown> }[] } | null,
  partyId: string | null,
): number | null {
  if (classifica === null || classifica.final !== true || partyId === null) return null;
  const riga = classifica.rows.find((r) => r.raw.id === partyId || r.raw.partyId === partyId);
  const posto = riga?.position ?? numero(riga?.raw.rank);
  return posto !== null && posto !== undefined && posto >= 1 ? posto : null;
}

export type TipoEsito = 'vinto' | 'perso' | 'torneo-vinto' | 'posto';

export interface EventoEsito {
  /** `m:<matchId>` per un incontro, `t:<tournamentId>` per una corsa: la chiave di «gia' vista». */
  chiave: string;
  tipo: TipoEsito;
  tournamentId: string;
  matchId: string | null;
  torneo: string | null;
  /**
   * Come si presenta la partita (`presentazioneDi`): una coda o un sit&go non dicono «torneo» (r15). Si ricalcola a ogni
   * esito dal torneo letto in quel momento, mai dalla memoria: lo schema `qlash.esiti.v1` non cambia.
   */
  presentazione: Presentazione;
  /** Il gioco del torneo (`LimuloTournament.gameId`), per tornare alla scheda giusta della Hub; `null` se non letto. */
  gameId: string | null;
  avversario: string | null;
  /** Solo per `posto`. */
  posto: number | null;
  /** L'ora dell'esito (`recordedAt`, ms), la scrive chi lo mette in fila: con lei si pota la vista. */
  quando: number | null;
}

/** Quando e' arrivato un esito, come lo dice la modale: `oggi` · `ieri` · `prima` (la data corta). */
export interface QuandoEsito {
  chiave: 'oggi' | 'ieri' | 'prima';
  /** I valori per la frase: `{ ora }` per oggi e ieri, `{ data }` per prima. Gia' nella lingua. */
  valori: { ora: string } | { data: string };
}

/**
 * L'ora dell'esito (`EventoEsito.quando`, ms) per la riga sotto il torneo, o `null` se non c'e' (niente riga).
 *
 * Perche' (I-328, Luca su stg-tma, 03/10): la modale di un esito VECCHIO e' comparsa mentre giocava una
 * coda nuova, e lui l'ha presa per l'esito di quella. L'ora dice di QUANDO e'.
 *
 * ⚠️ `ora` e' un PARAMETRO (l'orologio lo legge chi rende, una volta), la lingua anche, come in
 * `dateDisplay.ts`. «Oggi» e «ieri» sono giorni del CALENDARIO del telefono, non «meno di 24 ore fa»:
 * un esito delle 23:50 letto alle 00:20 e' di ieri. Un esito nel futuro (orologio del telefono indietro)
 * si dice «oggi»: non si inventa un «domani». `ESITI_FINESTRA_MS` e' 24 h, quindi `prima` capita di rado
 * (un orologio tornato indietro, o la vista che resta aperta oltre la mezzanotte).
 * ⛔ Non decide QUALI esiti si mostrano ne' in che ordine (`mostrabile`, la fila): e' un'altra decisione.
 */
export function quandoEsitoOf(quando: number | null, ora: number, lingua: string): QuandoEsito | null {
  if (quando === null || !Number.isFinite(quando)) return null;
  const giornoDi = (ms: number) => {
    const d = new Date(ms);
    return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 86_400_000;
  };
  const giorniFa = giornoDi(ora) - giornoDi(quando);
  if (giorniFa <= 0) return { chiave: 'oggi', valori: { ora: formatOra(quando, lingua) } };
  if (giorniFa === 1) return { chiave: 'ieri', valori: { ora: formatOra(quando, lingua) } };
  const altroAnno = new Date(quando).getFullYear() !== new Date(ora).getFullYear();
  return {
    chiave: 'prima',
    valori: {
      data: formatData(quando, { day: 'numeric', month: 'short', ...(altroAnno ? { year: 'numeric' } : {}) }, lingua),
    },
  };
}

/** L'esito e' mostrabile adesso? Dopo la base, entro le 24 ore, non gia' visto. */
export function mostrabile(memoria: MemoriaEsiti, chiave: string, quando: number | null, ora: number): boolean {
  return (
    quando !== null &&
    quando >= memoria.base &&
    ora - quando <= ESITI_FINESTRA_MS &&
    !memoria.viste.some((v) => v.chiave === chiave)
  );
}

/**
 * L'esito di un incontro. `torneo` e' il nome del torneo osservato, `letto` il torneo com'e' stato letto adesso (o `null`
 * se la lettura non e' arrivata: allora la presentazione ricade sul solo prefisso dell'id).
 */
export const eventoIncontro = (
  d: IncontroDeciso,
  s: Sparito,
  torneo: string | null,
  letto: LimuloTournament | null = null,
): EventoEsito => ({
  chiave: `m:${s.matchId}`,
  tipo: d.vinto ? 'vinto' : 'perso',
  tournamentId: s.tournamentId,
  matchId: s.matchId,
  torneo,
  presentazione: presentazioneDi(s.tournamentId, letto),
  gameId: letto?.gameId ?? null,
  avversario: d.avversario,
  posto: null,
  quando: d.quando,
});

/**
 * La fine della corsa, a torneo terminale e classifica finale. `null` se il posto non c'e' (torneo
 * annullato, classifica non definitiva, riga non trovata): allora non si dice niente sulla corsa.
 * 🔑 Nella coda 1v1 (`origine: 'coda'`) il torneo E' l'incontro: il titolo resta «Hai vinto / Hai perso»
 * (derivazione approvata dal seggio), mai «Hai vinto il torneo». Il sit&go ha un podio: titoli invariati.
 */
export function eventoCorsa(input: {
  tournamentId: string;
  torneo: (Pick<LimuloTournament, 'status' | 'title'> & Partial<Pick<LimuloTournament, 'gameId' | 'origine'>>) | null;
  corsa: CorsaOsservata;
  posto: number | null;
  matchId: string | null;
}): EventoEsito | null {
  if (input.torneo === null || storicoEsitoOf(input.torneo) !== 'concluso' || input.posto === null) return null;
  const presentazione = presentazioneDi(input.tournamentId, input.torneo);
  const coda = presentazione === 'coda';
  const primo = input.posto === 1;
  return {
    chiave: `t:${input.tournamentId}`,
    tipo: coda ? (primo ? 'vinto' : 'perso') : primo ? 'torneo-vinto' : 'posto',
    tournamentId: input.tournamentId,
    matchId: input.matchId,
    torneo: input.corsa.torneo ?? (testo(input.torneo.title) ?? null),
    presentazione,
    gameId: input.torneo.gameId ?? null,
    avversario: input.corsa.avversario,
    posto: coda || primo ? null : input.posto,
    quando: input.corsa.ultimo,
  };
}

/**
 * Due elenchi di viste → uno (revisione di #503, 🟡9): unione per chiave, fuori le piu' vecchie di 24 ore,
 * in ordine d'ESITO e tosato dal piu' vecchio. ⚠️ L'ordine e' la recenza, non la provenienza: messe «le
 * altrui in testa» (la prima stesura), al tetto la fusione buttava proprio le viste dell'altra scheda.
 */
export function fondiViste(a: readonly Vista[], b: readonly Vista[], ora: number): Vista[] {
  const perChiave = new Map<string, Vista>();
  for (const v of [...a, ...b]) {
    const c = perChiave.get(v.chiave);
    if (!c || v.quando > c.quando) perChiave.set(v.chiave, v);
  }
  return [...perChiave.values()]
    .filter((v) => ora - v.quando <= ESITI_FINESTRA_MS)
    .sort((x, y) => x.quando - y.quando)
    .slice(-ESITI_TETTO_VISTE);
}

/** La memoria dopo aver MOSTRATO un esito: entra fra le viste, con l'ora dell'esito. */
export const segnaVista = (memoria: MemoriaEsiti, chiave: string, quando: number, ora: number): MemoriaEsiti => ({
  ...memoria,
  viste: fondiViste(memoria.viste, [{ chiave, quando }], ora),
});

/** Via un incontro osservato (deciso, o uscito senza esito). */
export function dimenticaIncontro(memoria: MemoriaEsiti, matchId: string): MemoriaEsiti {
  if (!(matchId in memoria.osservati)) return memoria;
  const osservati = { ...memoria.osservati };
  delete osservati[matchId];
  return { ...memoria, osservati };
}

/** Via una corsa: e' finita (detta o no) o e' troppo vecchia per dirla. */
export function dimenticaCorsa(memoria: MemoriaEsiti, tournamentId: string): MemoriaEsiti {
  if (!(tournamentId in memoria.corse)) return memoria;
  const corse = { ...memoria.corse };
  delete corse[tournamentId];
  return { ...memoria, corse };
}

/** Il mio ultimo incontro deciso nel torneo, scritto sulla corsa. */
export const ricordaIncontro = (memoria: MemoriaEsiti, tournamentId: string, d: IncontroDeciso): MemoriaEsiti => ({
  ...memoria,
  corse: {
    ...memoria.corse,
    [tournamentId]: {
      torneo: memoria.corse[tournamentId]?.torneo ?? null,
      partyId: d.partyId,
      ultimo: d.quando,
      avversario: d.avversario,
    },
  },
});

/** Ho ancora incontri osservati in quel torneo? Allora la corsa non e' finita. */
export const corsaAperta = (memoria: MemoriaEsiti, tournamentId: string): boolean =>
  Object.values(memoria.osservati).some((o) => o.tournamentId === tournamentId);

/**
 * Le corse da ricontrollare: conosco la mia parte, non ho incontri aperti li', e l'ultimo incontro e'
 * ancora nelle 24 ore. Le altre (troppo vecchie) si possono dimenticare: `scadute`.
 */
export function corseInAttesa(memoria: MemoriaEsiti, ora: number): { attesa: string[]; scadute: string[] } {
  const attesa: string[] = [];
  const scadute: string[] = [];
  for (const [id, c] of Object.entries(memoria.corse)) {
    if (c.partyId === null || corsaAperta(memoria, id)) continue;
    if (c.ultimo === null || ora - c.ultimo > ESITI_FINESTRA_MS) scadute.push(id);
    else attesa.push(id);
  }
  return { attesa, scadute };
}

/**
 * Una schermata di gioco dal vivo nella TMA: la partita di backgammon e quella di chinese poker. Li'
 * l'esito ASPETTA (seggio, condizione 2). ⚠️ `/backgammon/live/:id` e' lo spettatore: non si gioca.
 */
export const schermataDiGioco = (pathname: string): boolean =>
  /^\/(backgammon|chinesepoker)\/match\/[^/]+\/?$/.test(pathname);
